import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Heart, ChevronsUpDown, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';

/**
 * Compact modal used from the dashboard widget (and anywhere else you want the
 * "give a vote" action). Loads the internal team list on open, excludes the
 * current user + Clients, and posts the vote.
 *
 * Props:
 *  - open, onOpenChange
 *  - currentUserId (excluded from picker)
 *  - onGiven({ vote, votes_remaining_today })  // called on success
 */
export default function GiveVoteModal({ open, onOpenChange, currentUserId, onGiven }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [toUserId, setToUserId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToUserId('');
    setNote('');
    setLoadingUsers(true);
    api
      .get('/users')
      .then((r) => {
        // Only active internal teammates, excluding self + Clients.
        const list = (r.data || []).filter(
          (u) => u.role !== 'Client' && u.id !== currentUserId && (u.status || 'active') === 'active',
        );
        setUsers(list);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [open, currentUserId]);

  const selected = useMemo(() => users.find((u) => u.id === toUserId), [users, toUserId]);

  const initials = (name) => (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  const submit = async () => {
    if (!toUserId) {
      toast.error('Pick a teammate first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/votes', { to_user_id: toUserId, note: (note || '').trim() });
      toast.success(`Vote sent to ${selected?.name || 'teammate'} 🎉`);
      onGiven && onGiven(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the vote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Heart className="h-5 w-5 text-amber-300" /> Give a peer vote
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-slate-400 -mt-1">
            Only the teammate you pick will know it was you. Nobody else on the team sees your vote — Owner &amp; Admin can see everything for oversight.
          </p>
          <div>
            <Label>Recognise which teammate?</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="vote-recipient-trigger"
                  className="w-full justify-between bg-[#0f2240] border-amber-500/20 hover:bg-[#0f2240] hover:border-amber-500/40 text-white font-normal"
                >
                  {selected ? (
                    <span className="flex items-center gap-2 truncate">
                      <span className="inline-flex h-5 w-5 rounded-full bg-amber-500/20 items-center justify-center text-[10px] font-bold text-amber-100">
                        {initials(selected.name)}
                      </span>
                      <span className="truncate">{selected.name}</span>
                      <span className="text-slate-500 text-xs truncate">· {selected.role}</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">{loadingUsers ? 'Loading teammates…' : 'Pick a teammate'}</span>
                  )}
                  {loadingUsers ? (
                    <Loader2 className="h-4 w-4 animate-spin opacity-60" />
                  ) : (
                    <ChevronsUpDown className="h-4 w-4 opacity-60" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 bg-[#0a1628] border-amber-500/20 text-slate-100"
                style={{ width: 'var(--radix-popover-trigger-width)' }}
              >
                <Command className="bg-transparent">
                  <CommandInput placeholder="Search teammates…" className="text-slate-100 placeholder:text-slate-500" data-testid="vote-recipient-search" />
                  <CommandList>
                    <CommandEmpty className="text-slate-400 py-4 text-sm text-center">No teammates found.</CommandEmpty>
                    <CommandGroup>
                      {users.map((u) => (
                        <CommandItem
                          key={u.id}
                          value={`${u.name} ${u.role} ${u.email || ''}`}
                          onSelect={() => { setToUserId(u.id); setPickerOpen(false); }}
                          data-testid={`vote-recipient-option-${u.id}`}
                          className={`cursor-pointer text-slate-100 aria-selected:bg-amber-500/15 aria-selected:text-white ${toUserId === u.id ? 'bg-amber-500/5' : ''}`}
                        >
                          <span className="inline-flex h-6 w-6 rounded-full bg-amber-500/15 items-center justify-center text-[10px] font-bold text-amber-100 mr-2 flex-shrink-0">
                            {initials(u.name)}
                          </span>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="truncate font-medium">{u.name}</span>
                            <span className="text-[11px] text-slate-400 truncate">{u.role}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Optional note <span className="text-slate-500 text-xs">— what made you notice them?</span></Label>
            <Textarea
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Nailed the client presentation this morning."
              className="bg-[#0f2240] border-amber-500/20 text-white"
              data-testid="vote-note-input"
            />
            <div className="text-[10px] text-slate-500 text-right mt-1">{note.length}/500</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} className="text-slate-300 hover:text-white">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!toUserId || submitting}
            data-testid="vote-submit-btn"
            className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send vote'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
